<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-comment-quote me-2"></i>{{ $t('page.patient_feedback', 'Patient Feedback & Complaints') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button class="hms-btn hms-btn-success" @click="openSubmitModal()"><i class="mdi mdi-plus"></i> Capture feedback</button>
          <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.new_count || 0 }}</div><div class="hms-stat-lbl">New</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.in_progress || 0 }}</div><div class="hms-stat-lbl">In Progress</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.resolved || 0 }}</div><div class="hms-stat-lbl">Resolved (90d)</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.escalated || 0 }}</div><div class="hms-stat-lbl">Escalated</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ stats.avg_overall != null ? stats.avg_overall + '/5' : '—' }}</div><div class="hms-stat-lbl">Avg Rating</div></div>
      </div>

      <div class="hms-grid-2 mb-3">
        <div class="hms-card">
          <div class="hms-card-head">Net Promoter Score (90d)</div>
          <div class="hms-card-body">
            <div class="hms-nps" :class="npsClass">
              <div class="hms-nps-num">{{ nps != null ? nps : '—' }}</div>
              <div class="hms-muted-sm">{{ stats.nps_responses || 0 }} responses</div>
            </div>
            <div class="hms-nps-row mt-2">
              <span class="hms-nps-promoters">Promoters (9-10): {{ stats.nps_promoters || 0 }}</span>
              <span class="hms-nps-detractors">Detractors (0-6): {{ stats.nps_detractors || 0 }}</span>
            </div>
          </div>
        </div>
        <div class="hms-card">
          <div class="hms-card-head">By department (90d)</div>
          <div class="hms-card-body p-0">
            <table v-if="byDept.length" class="hms-table">
              <thead><tr><th>Department</th><th>Feedback</th><th>Avg rating</th><th>Complaints</th></tr></thead>
              <tbody>
                <tr v-for="d in byDept" :key="d.department">
                  <td><strong>{{ d.department || '—' }}</strong></td>
                  <td>{{ d.n }}</td>
                  <td>{{ d.avg_rating != null ? d.avg_rating + '/5' : '—' }}</td>
                  <td :class="d.complaints > 0 ? 'st-bad' : ''">{{ d.complaints }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="hms-pad text-center hms-muted">No feedback in last 90 days.</div>
          </div>
        </div>
      </div>

      <!-- FEEDBACK LIST -->
      <div class="hms-card">
        <div class="hms-card-head d-flex justify-content-between">
          <span>Feedback log ({{ feedback.length }})</span>
          <div class="d-flex gap-2">
            <select v-model="statusFilter" class="hms-input hms-input-sm" @change="loadAll">
              <option value="">All statuses</option>
              <option value="new">New</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="in_progress">In progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
              <option value="escalated">Escalated</option>
            </select>
            <select v-model="typeFilter" class="hms-input hms-input-sm" @change="loadAll">
              <option value="">All types</option>
              <option value="satisfaction">Satisfaction</option>
              <option value="complaint">Complaint</option>
              <option value="compliment">Compliment</option>
              <option value="suggestion">Suggestion</option>
              <option value="grievance">Grievance</option>
            </select>
            <select v-model="serviceLineFilter" class="hms-input hms-input-sm" @change="loadAll">
              <option value="">All service lines</option>
              <option value="opd">Outpatient (OPD)</option>
              <option value="ipd">Inpatient (IPD)</option>
              <option value="maternity">Maternity</option>
              <option value="surgery">Surgery / Theatre</option>
              <option value="casualty">Casualty / A&E</option>
              <option value="pharmacy">Pharmacy</option>
              <option value="lab">Laboratory</option>
              <option value="radiology">Radiology</option>
              <option value="dental">Dental</option>
              <option value="eye">Eye</option>
              <option value="mental_health">Mental Health</option>
              <option value="ccc">CCC (HIV)</option>
              <option value="tb">TB Clinic</option>
              <option value="mch">MCH Clinic</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div class="hms-card-body p-0">
          <table class="hms-table">
            <thead><tr><th>When</th><th>Patient</th><th>Department</th><th>Type</th><th>Channel</th><th>Rating</th><th>NPS</th><th>Comment</th><th>Status</th><th></th></tr></thead>
            <tbody>
              <tr v-for="f in feedback" :key="f.feedback_id" :class="rowClass(f)">
                <td>{{ f.submitted_at }}</td>
                <td>{{ f.patient_name || 'Anonymous' }}<div class="hms-muted-sm">{{ f.patient_no || '' }}</div></td>
                <td>{{ f.department || '—' }}</td>
                <td><span class="hms-pill" :class="typePill(f.feedback_type)">{{ f.feedback_type }}</span></td>
                <td><span class="hms-muted-sm">{{ f.channel }}</span></td>
                <td>
                  <span v-if="f.overall_rating">{{ '★'.repeat(f.overall_rating) }}{{ '☆'.repeat(5 - f.overall_rating) }}</span>
                  <span v-else class="hms-muted-sm">—</span>
                </td>
                <td>{{ f.nps_score != null ? f.nps_score + '/10' : '—' }}</td>
                <td><span class="hms-comment">{{ truncate(f.comments, 80) }}</span></td>
                <td><span class="hms-pill" :class="statusPill(f.status)">{{ f.status }}</span></td>
                <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openHandleModal(f)"><i class="mdi mdi-pencil"></i></button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ===== SUBMIT MODAL ===== -->
    <div v-if="submitModal" class="hms-modal-bg" @click.self="submitModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>Capture patient feedback</h5><button class="hms-icon-btn" @click="submitModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Type</label>
              <select v-model="form.feedback_type" class="hms-input">
                <option value="satisfaction">Satisfaction survey</option>
                <option value="complaint">Complaint</option>
                <option value="compliment">Compliment</option>
                <option value="suggestion">Suggestion</option>
                <option value="grievance">Grievance</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Channel</label>
              <select v-model="form.channel" class="hms-input">
                <option value="tablet">Tablet (in-facility)</option>
                <option value="paper">Paper</option>
                <option value="sms">SMS</option>
                <option value="web">Web</option>
                <option value="phone">Phone</option>
                <option value="exit_interview">Exit interview</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Department</label>
              <select v-model="form.department" class="hms-input">
                <option value="">— select —</option>
                <option value="OPD">OPD</option><option value="ED">Emergency</option><option value="Casualty">Casualty</option>
                <option value="Maternity">Maternity</option><option value="Theatre">Theatre</option>
                <option value="Lab">Lab</option><option value="Pharmacy">Pharmacy</option>
                <option value="Radiology">Radiology</option><option value="Wards">Wards</option>
                <option value="ICU">ICU</option><option value="NICU">NICU</option>
                <option value="Reception">Reception</option><option value="Billing">Billing</option>
                <option value="General">General</option>
              </select>
            </div>
            <div class="col-6 d-flex align-items-end"><label><input type="checkbox" v-model="form.is_anonymous" :true-value="1" :false-value="0" /> Anonymous</label></div>
            <div class="col-12" v-if="form.is_anonymous == 0">
              <label class="hms-label">Patient (optional)</label>
              <div class="d-flex gap-2">
                <input v-model="form.patient_search" class="hms-input" placeholder="Name / patient #" @keyup.enter="findPatient" />
                <button class="hms-btn hms-btn-blue" @click="findPatient">Find</button>
              </div>
              <div v-if="form.patient_id" class="hms-patient-chip mt-1">
                <i class="mdi mdi-account"></i> {{ form.patient_name }}
                <a href="javascript:void(0)" class="ms-2" @click="form.patient_id=null; form.patient_name=''">change</a>
              </div>
            </div>

            <div class="col-12"><div class="hms-section-label">RATINGS (1-5)</div></div>
            <div class="col-2"><label class="hms-label">Overall</label><input v-model.number="form.overall_rating" type="number" min="1" max="5" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Wait time</label><input v-model.number="form.wait_time_rating" type="number" min="1" max="5" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Staff</label><input v-model.number="form.staff_rating" type="number" min="1" max="5" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Cleanliness</label><input v-model.number="form.cleanliness_rating" type="number" min="1" max="5" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Cost</label><input v-model.number="form.cost_rating" type="number" min="1" max="5" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">NPS (0-10)</label><input v-model.number="form.nps_score" type="number" min="0" max="10" class="hms-input" /></div>

            <div class="col-12"><label class="hms-label">Comments</label><textarea v-model="form.comments" class="hms-input" rows="4"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="submitModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveSubmit">{{ saving?'Submitting…':'Submit' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== HANDLE MODAL ===== -->
    <div v-if="handleModal" class="hms-modal-bg" @click.self="handleModal=false">
      <div class="hms-modal" style="width:600px">
        <div class="hms-modal-head"><h5>Handle feedback</h5><button class="hms-icon-btn" @click="handleModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-info-banner">
            <strong>{{ handleItem.feedback_type }}</strong> from {{ handleItem.patient_name || 'Anonymous' }} ({{ handleItem.department || '—' }}, {{ handleItem.submitted_at }})
            <div v-if="handleItem.overall_rating" class="hms-muted-sm">{{ '★'.repeat(handleItem.overall_rating) }}{{ '☆'.repeat(5 - handleItem.overall_rating) }} · NPS {{ handleItem.nps_score != null ? handleItem.nps_score + '/10' : '—' }}</div>
            <div v-if="handleItem.comments" class="mt-2"><em>"{{ handleItem.comments }}"</em></div>
          </div>
          <div class="row g-2 mt-2">
            <div class="col-6"><label class="hms-label">New status</label>
              <select v-model="handleForm.status" class="hms-input">
                <option value="acknowledged">Acknowledge</option>
                <option value="in_progress">In progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
                <option value="escalated">Escalate</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Resolution / action notes</label><textarea v-model="handleForm.resolution_notes" class="hms-input" rows="3"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="handleModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveHandle">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "PatientFeedback",
  components: { Navigation },
  data() {
    return {
      saving: false,
      stats: {}, nps: null, byDept: [],
      feedback: [],
      statusFilter: '', typeFilter: '', serviceLineFilter: '',

      submitModal: false, form: this.blankForm(),
      handleModal: false, handleItem: {}, handleForm: { status: 'acknowledged', resolution_notes: '' }
    };
  },
  computed: {
    npsClass() {
      if (this.nps == null) return '';
      if (this.nps >= 50) return 'nps-good';
      if (this.nps >= 0) return 'nps-mid';
      return 'nps-bad';
    }
  },
  mounted() { this.loadAll(); },
  methods: {
    blankForm() {
      return {
        patient_id: null, patient_name: '', patient_search: '',
        visit_id: null, admission_id: null,
        feedback_type: 'satisfaction', channel: 'tablet', department: '',
        overall_rating: 5, wait_time_rating: null, staff_rating: null, cleanliness_rating: null, cost_rating: null,
        nps_score: 9, comments: '', is_anonymous: 0
      };
    },

    async loadAll() {
      try {
        const r = await executeJson({ function: 'getPatientFeedback', status: this.statusFilter, feedback_type: this.typeFilter }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.feedback = d.Feedback || [];
          this.stats = d.Stats || {};
          this.nps = d.NPS;
          this.byDept = d.ByDept || [];
        }
      } catch (e) { console.error(e); }
    },

    openSubmitModal() { this.form = this.blankForm(); this.submitModal = true; },
    async findPatient() {
      const q = (this.form.patient_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const p = ((r.data.data && (r.data.data.Patients || r.data.data.patients)) || [])[0];
        if (!p) { alert('No patient found.'); return; }
        this.form.patient_id = p.patient_id;
        this.form.patient_name = p.first_name + ' ' + p.last_name;
      }
    },
    async saveSubmit() {
      this.saving = true;
      try {
        const params = { function: 'submitPatientFeedback', ...this.form };
        delete params.patient_search; delete params.patient_name;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) { this.submitModal = false; await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openHandleModal(f) {
      this.handleItem = f;
      this.handleForm = { status: f.status === 'new' ? 'acknowledged' : 'in_progress', resolution_notes: f.resolution_notes || '' };
      this.handleModal = true;
    },
    async saveHandle() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'updateFeedbackStatus', feedback_id: this.handleItem.feedback_id, ...this.handleForm }, hms);
        if (r.data && r.data.success) { this.handleModal = false; await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    rowClass(f) {
      if (f.feedback_type === 'grievance' || f.status === 'escalated') return 'row-action';
      if (f.feedback_type === 'complaint' && f.status === 'new') return 'row-warn';
      return '';
    },
    typePill(t) { return ({ satisfaction: 'hms-pill-info', complaint: 'hms-pill-warn', compliment: 'hms-pill-success', suggestion: 'hms-pill-info', grievance: 'hms-pill-danger' })[t] || ''; },
    statusPill(s) { return ({ new: 'hms-pill-warn', acknowledged: 'hms-pill-info', in_progress: 'hms-pill-info', resolved: 'hms-pill-success', closed: 'hms-pill-muted', escalated: 'hms-pill-danger' })[s] || ''; },
    truncate(s, n) { if (!s) return ''; return s.length > n ? s.substring(0,n) + '…' : s; }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 26px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-success { color: #276749; }
.hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

.hms-section-label { font-size: 11px; font-weight: 700; color: #4a5568; letter-spacing: 1px; padding: 4px 0; border-bottom: 1px solid #edf2f7; margin: 8px 0 6px; }

.hms-nps { padding: 16px; text-align: center; border-radius: 8px; }
.hms-nps.nps-good { background: #f0fff4; color: #276749; }
.hms-nps.nps-mid  { background: #fffaf0; color: #b45309; }
.hms-nps.nps-bad  { background: #fff5f5; color: #c53030; }
.hms-nps-num { font-size: 48px; font-weight: 800; line-height: 1; }
.hms-nps-row { display: flex; justify-content: space-between; font-size: 13px; }
.hms-nps-promoters { color: #276749; font-weight: 600; }
.hms-nps-detractors { color: #c53030; font-weight: 600; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-input.hms-input-sm { padding: 3px 8px; font-size: 12px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-comment { color: #4a5568; font-style: italic; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }
.hms-info-banner { background: #ebf8ff; border: 1px solid #90cdf4; color: #2c5282; padding: 10px 12px; border-radius: 6px; font-size: 13px; }
.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; font-size: 13px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }

.st-bad { color: #c53030; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
.ms-2 { margin-left: 8px; }
</style>
